/**
 * Step 4 of the templates path — "Pick a layout for each slide".
 *
 * Shows the outline the AI sketched, one row per slide, each row carrying the
 * layout that slide will be filled with. Rows default to the ranker's pick
 * (badged "Auto: <name>"); clicking one opens the swap gallery. A slide the
 * library has no candidate for is badged as needing a choice and blocks
 * "Next" — building it from a layout that can't be fetched would produce a
 * slide the assembler silently couldn't patch.
 *
 * THE SWAP GALLERY DEFAULTS TO THE OUTLINE'S OWN SUGGESTIONS, NEVER THE WHOLE
 * LIBRARY. The endpoint already ranked a handful of candidates per slide
 * (`recommended_template` + `alternatives`) — that's what opens, every time,
 * regardless of whether the full catalog has finished loading. Browsing the
 * rest of the library (which can run into the thousands of entries) is an
 * explicit "Browse the full library" action one level down, not something
 * dumped on screen just because its manifest happened to load fast. See
 * ChooseLayoutGallery / SuggestedLayoutsGrid.
 *
 * All state lives in hooks/useTemplatePicker.ts (which owns the two loads and
 * the picks); this file is presentation only. The gallery is a full-step view
 * rather than a nested popover so the cards are big enough to judge a layout
 * by, which is the whole point of swapping one.
 */

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Grid, type CellComponentProps } from "react-window";
import type { SlideTemplate } from "../../slide-templates/services/slideTemplatesApi";
import type { SlidePreview } from "../../shared/services/slidePreviewGeometry";
import { nameFromTemplateId, type CatalogLoadState, type SlideLayoutChoice, type TemplatePicker } from "../hooks/useTemplatePicker";
import type { TemplateMatch } from "../service/templatesApi";
import styles from "./CreateSlide.module.css";

/** Bullet lines shown under a row's title before it collapses into "+N more". */
const MAX_ROW_BULLETS = 2;

/* ── Swap-gallery virtualization ─────────────────── */

/** Matches .galCard's own padding/name/category chrome in CreateSlide.module.css —
 *  everything in a card's box that ISN'T the thumbnail, so a card's total
 *  height can be predicted from its width alone (thumb is a fixed 16:10). */
const GAL_CARD_CHROME_HEIGHT = 48;
const GAL_CARD_PADDING = 6;
const GAL_GRID_GUTTER = 8;
/** Matches .galGrid's own `minmax(150px, 1fr)` — the floor a column may shrink to
 *  before another column is dropped, same responsiveness as the CSS grid this
 *  replaces. */
const GAL_MIN_COLUMN_WIDTH = 150;
const GAL_THUMB_ASPECT = 10 / 16;
/** Assumed width until the container is actually measured — a brand-new
 *  ResizeObserver hasn't reported yet on the very first paint, and a test
 *  environment with no layout engine (jsdom) never reports a real one at
 *  all. Close enough to a typical dialog's content width to look right
 *  either way, rather than rendering nothing until a real number arrives. */
const GAL_DEFAULT_GRID_WIDTH = 640;
/** Stable empty array so useMemo dependencies don't churn on every render
 *  while the full catalog hasn't loaded yet (a fresh `[]` literal would be a
 *  new reference each time). */
const EMPTY_TEMPLATES: readonly SlideTemplate[] = [];

/**
 * A library can carry well over a thousand entries (a real customer catalog
 * routinely does) — rendering that many cards into the DOM at once is what
 * made the gallery feel stuck, not the manifest fetch itself. `react-window`
 * keeps the DOM to roughly what's on screen; the CSS grid it replaces is
 * otherwise unaffected (col count/gutter still mirror its own values above).
 */
function useElementWidth<T extends HTMLElement>(): [React.MutableRefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/* ── Layout wireframe ───────────────────────────── */

/**
 * The entry's placeholder rectangles, drawn as a wireframe. Same approach as
 * the Slide Templates panel's own `LayoutWireframe`: the viewBox is a flat
 * 0-100 square with `preserveAspectRatio="none"`, because blocks are already
 * percentages of their own axis, so letting each axis stretch independently
 * to a correctly-proportioned box is what puts them in the right place.
 */
function LayoutWireframe({ preview }: Readonly<{ preview: SlidePreview }>) {
  return (
    <svg
      className={styles.olWire}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {preview.blocks.map((block, index) => (
        <rect
          // Blocks carry no ids; their own geometry is what makes them unique
          // within one preview (identical rects are deduped upstream).
          key={`${block.role}-${block.x}-${block.y}-${block.w}-${block.h}-${index}`}
          className={block.role === "title" ? styles.olWireTitle : undefined}
          x={block.x}
          y={block.y}
          width={block.w}
          height={block.h}
          rx={1}
        />
      ))}
    </svg>
  );
}

/** Real thumbnail when the library publishes one, else the wireframe, else
 *  an empty box (never a broken image). */
function LayoutThumb({ template }: Readonly<{ template: SlideTemplate | null }>) {
  let inner = null;
  if (template?.thumbnailUrl) {
    inner = <img className={styles.olThumbImg} src={template.thumbnailUrl} alt="" loading="lazy" />;
  } else if (template?.preview) {
    inner = <LayoutWireframe preview={template.preview} />;
  }
  return <span className={styles.olThumb}>{inner}</span>;
}

/* ── The outline list ───────────────────────────── */

function SlideRow({
  choice,
  position,
  isFirst,
  isLast,
  canRemove,
  onChoose,
  onMoveUp,
  onMoveDown,
  onRemove,
}: Readonly<{
  choice: SlideLayoutChoice;
  position: number;
  isFirst: boolean;
  isLast: boolean;
  /** False once only one slide remains — a deck needs at least one. */
  canRemove: boolean;
  onChoose: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}>) {
  const { slide, template, templateId, autoPicked, layoutName } = choice;
  const bullets = slide.main_points ?? [];
  const needsPick = templateId === null;

  let badge: string;
  if (needsPick) {
    badge = "Choose a layout";
  } else {
    badge = autoPicked ? `Auto: ${layoutName}` : layoutName;
  }

  return (
    <div className={`${styles.olRow} ${needsPick ? styles.olRowNeedsPick : ""}`}>
      <span className={styles.olNumWrap}>
        <span className={styles.olNum}>{position}</span>
        <span className={styles.olNumCat}>{slide.intent_tag}</span>
        <span className={styles.olMoveBtns}>
          <button
            type="button"
            className={styles.olMoveBtn}
            onClick={onMoveUp}
            disabled={isFirst}
            aria-label={`Move slide ${position} up`}
          >
            ↑
          </button>
          <button
            type="button"
            className={styles.olMoveBtn}
            onClick={onMoveDown}
            disabled={isLast}
            aria-label={`Move slide ${position} down`}
          >
            ↓
          </button>
        </span>
      </span>
      <LayoutThumb template={template} />
      <span className={styles.olBody}>
        <span className={styles.olTitle}>{slide.title}</span>
        {bullets.slice(0, MAX_ROW_BULLETS).map((point) => (
          <span key={point} className={styles.olBullet}>
            {point}
          </span>
        ))}
        {bullets.length > MAX_ROW_BULLETS && (
          <span className={styles.olMore}>+{bullets.length - MAX_ROW_BULLETS} more</span>
        )}
      </span>
      <span className={styles.olRight}>
        <span className={needsPick ? styles.olNeedsPick : styles.olAutoPick}>{badge}</span>
        <span className={styles.olRowActions}>
          <button type="button" className={styles.olChooseBtn} onClick={onChoose}>
            {needsPick ? "Pick layout" : "Change"}
          </button>
          {canRemove && (
            <button type="button" className={styles.olRemoveBtn} onClick={onRemove}>
              Remove
            </button>
          )}
        </span>
      </span>
    </div>
  );
}

/* ── The swap gallery ───────────────────────────── */

/** Layout families offered as filter chips, built from what the library
 *  actually holds so a chip never yields an empty grid. */
function categoryChips(templates: readonly SlideTemplate[]): Array<{ id: string; count: number }> {
  const counts = new Map<string, number>();
  for (const template of templates) {
    const key = template.layoutClass ?? template.category;
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
}

/** Label for the "browse the full library" opt-in button — reflects why it's
 *  disabled (still loading vs. failed) rather than a single generic label. The
 *  library isn't downloaded until this button is first clicked. */
function browseLibraryLabel(catalogState: CatalogLoadState, templateCount: number): string {
  if (catalogState.status === "ready") return `Browse the full library (${templateCount})`;
  if (catalogState.status === "idle") return "Browse the full library";
  return catalogState.status === "error" ? "Full library unavailable" : "Loading the full library…";
}

function matchesQuery(template: SlideTemplate, query: string): boolean {
  if (!query) return true;
  const haystack = [template.name, template.category, template.layoutClass, template.description]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

/** Extra props `Grid` hands to every cell beyond {columnIndex, rowIndex, style}. */
interface GalleryCellProps {
  readonly ordered: readonly SlideTemplate[];
  readonly columnCount: number;
  readonly activeTemplateId: string | null;
  readonly suggestionScoreById: ReadonlyMap<string, number>;
  readonly onPick: (templateId: string) => void;
}

/**
 * One virtualized cell. `columnIndex`/`rowIndex` address into the flat
 * `ordered` array; a trailing row can run short of `columnCount` (the grid is
 * rectangular, the data isn't), so an out-of-range index renders nothing
 * rather than crash.
 */
function GalleryCell({
  columnIndex,
  rowIndex,
  style,
  ordered,
  columnCount,
  activeTemplateId,
  suggestionScoreById,
  onPick,
}: CellComponentProps<GalleryCellProps>) {
  const template = ordered[rowIndex * columnCount + columnIndex];
  if (!template) return null;
  const score = suggestionScoreById.get(template.id);

  return (
    <div style={style}>
      <button
        type="button"
        // Inset from the cell's own edges rather than a margin: `Grid` sizes
        // cells edge-to-edge, so this is what recreates the CSS grid's `gap`.
        style={{ position: "absolute", inset: GAL_GRID_GUTTER / 2 }}
        className={`${styles.galCard} ${template.id === activeTemplateId ? styles.galCardActive : ""}`}
        onClick={() => onPick(template.id)}
        title={template.description ?? template.name}
        aria-pressed={template.id === activeTemplateId}
      >
        <span className={styles.galThumb}>
          {template.thumbnailUrl ? (
            <img className={styles.olThumbImg} src={template.thumbnailUrl} alt="" loading="lazy" />
          ) : (
            template.preview && <LayoutWireframe preview={template.preview} />
          )}
          {/* Only a ranked candidate has a score to show — an unranked
              layout from the library at large has nothing to match against. */}
          {score !== undefined && <span className={styles.galScoreBadge}>{Math.round(score * 100)}%</span>}
        </span>
        <span className={styles.galCardName}>{template.name}</span>
        <span className={styles.galCardCat}>
          {score !== undefined ? "Recommended for this slide" : template.layoutClass ?? template.category ?? ""}
        </span>
      </button>
    </div>
  );
}

/**
 * One of the outline's own ranked candidates. Enriched with the matching
 * library entry (real thumbnail, canonical name) once/if the catalog has
 * loaded and holds it — but never GATED on that: the id/name/score are
 * already real, straight from the outline response, so this is pickable
 * from the very first render. The actual template file is resolved
 * independently at generation time (see service/templateGenerator.ts),
 * never from this gallery's own (possibly still-loading or never-browsed)
 * catalog.
 */
function SuggestionCard({
  suggestion,
  template,
  isActive,
  onPick,
}: Readonly<{
  suggestion: TemplateMatch;
  template: SlideTemplate | null;
  isActive: boolean;
  onPick: (templateId: string) => void;
}>) {
  return (
    <button
      type="button"
      className={`${styles.galCard} ${isActive ? styles.galCardActive : ""}`}
      onClick={() => onPick(suggestion.id)}
      title={template?.description ?? suggestion.name ?? suggestion.id}
      aria-pressed={isActive}
    >
      <span className={styles.galThumb}>
        {template?.thumbnailUrl ? (
          <img className={styles.olThumbImg} src={template.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          template?.preview && <LayoutWireframe preview={template.preview} />
        )}
        <span className={styles.galScoreBadge}>{Math.round(suggestion.score * 100)}%</span>
      </span>
      <span className={styles.galCardName}>{template?.name ?? suggestion.name ?? nameFromTemplateId(suggestion.id)}</span>
      <span className={styles.galCardCat}>Recommended for this slide</span>
    </button>
  );
}

/**
 * The DEFAULT swap view: only the ids the outline endpoint itself suggested
 * for this slide (`recommended_template` + `alternatives`) — never the full
 * library. That's a handful of cards at most, so no search/filter chrome and
 * no virtualization; browsing the rest of the (possibly 1000+ entry) catalog
 * is an explicit opt-in one level down (see ChooseLayoutGallery).
 */
function SuggestedLayoutsGrid({
  suggestions,
  templatesById,
  activeTemplateId,
  onPick,
}: Readonly<{
  suggestions: readonly TemplateMatch[];
  templatesById: ReadonlyMap<string, SlideTemplate>;
  activeTemplateId: string | null;
  onPick: (templateId: string) => void;
}>) {
  if (suggestions.length === 0) {
    return <p className={styles.olHint}>The ranker had no suggestions for this slide — browse the full library instead.</p>;
  }
  return (
    <div className={styles.galQuickPickGrid}>
      {suggestions.map((suggestion) => (
        <SuggestionCard
          key={suggestion.id}
          suggestion={suggestion}
          template={templatesById.get(suggestion.id) ?? null}
          isActive={suggestion.id === activeTemplateId}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

function ChooseLayoutGallery({
  choice,
  position,
  catalogState,
  templatesById,
  onPick,
  onBack,
  onUseAuto,
  onRetryCatalog,
  onBrowseLibrary,
}: Readonly<{
  choice: SlideLayoutChoice;
  position: number;
  catalogState: CatalogLoadState;
  /** Every template known so far, by id — includes outline-suggested ids the
   *  manifest stream has already found even before `catalogState` reaches
   *  "ready" (see useTemplatePicker.ts's module header). What the default
   *  suggestions view enriches its cards from; the full grid below reads
   *  straight off `catalogState.templates` instead, since it only ever shows
   *  once that's ready anyway. */
  templatesById: ReadonlyMap<string, SlideTemplate>;
  onPick: (templateId: string) => void;
  onBack: () => void;
  onUseAuto: (() => void) | null;
  onRetryCatalog: () => void;
  /** Starts the full library download — the only thing that ever does. */
  onBrowseLibrary: () => void;
}>) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  // The DEFAULT view is the outline's own suggestions ONLY — never the whole
  // library, however fast its manifest loaded. Browsing the rest is an
  // explicit choice, one level down, not something dumped on screen by
  // default just because the (possibly 1000+ entry) catalog happened to be
  // ready. See the module header / SuggestedLayoutsGrid.
  const [browsingFull, setBrowsingFull] = useState(false);

  const catalogReady = catalogState.status === "ready";
  // Browsable ONLY once the full library has loaded — until then this stays
  // empty. See this component's render: `ordered` (and everything derived
  // from it) simply isn't used until the user opts into browsingFull, but
  // every hook below still runs unconditionally.
  const templates = catalogReady ? catalogState.templates : EMPTY_TEMPLATES;

  const chips = useMemo(() => categoryChips(templates), [templates]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return templates.filter(
      (template) =>
        matchesQuery(template, needle) &&
        (category === null || (template.layoutClass ?? template.category) === category),
    );
  }, [templates, query, category]);

  // The ranker's own candidates float to the top: they're the layouts that
  // actually suit this slide's shape, and hunting for them in a 100+ card
  // grid would defeat the point of ranking them. Keyed by score (not just
  // membership) so a card can also show HOW well it matches, not just that it
  // matches.
  const suggestionScoreById = useMemo(
    () => new Map(choice.suggestions.map((suggestion) => [suggestion.id, suggestion.score])),
    [choice.suggestions],
  );
  const ordered = useMemo(
    () =>
      [...visible].sort(
        (a, b) => Number(!suggestionScoreById.has(a.id)) - Number(!suggestionScoreById.has(b.id)),
      ),
    [visible, suggestionScoreById],
  );

  // Virtualized in place of a plain CSS grid: a real catalog routinely runs
  // into the thousands of entries, and mounting a DOM card (plus thumbnail
  // image or wireframe SVG) for every one of them — even off-screen — is what
  // made this feel stuck. Column math mirrors .galGrid's own
  // `repeat(auto-fill, minmax(150px, 1fr))` + 8px gap, so the visible layout
  // is unchanged; only what's actually in the DOM is smaller.
  const [gridWrapRef, measuredGridWidth] = useElementWidth<HTMLDivElement>();
  const gridWidth = measuredGridWidth || GAL_DEFAULT_GRID_WIDTH;
  const columnCount = Math.max(1, Math.floor((gridWidth + GAL_GRID_GUTTER) / (GAL_MIN_COLUMN_WIDTH + GAL_GRID_GUTTER)));
  const columnWidth = gridWidth / columnCount;
  const rowHeight =
    (columnWidth - GAL_GRID_GUTTER - GAL_CARD_PADDING * 2) * GAL_THUMB_ASPECT + GAL_CARD_CHROME_HEIGHT + GAL_GRID_GUTTER;
  const rowCount = Math.ceil(ordered.length / columnCount);
  const cellProps = useMemo<GalleryCellProps>(
    () => ({ ordered, columnCount, activeTemplateId: choice.templateId, suggestionScoreById, onPick }),
    [ordered, columnCount, choice.templateId, suggestionScoreById, onPick],
  );

  const showFullGrid = browsingFull && catalogReady;

  return (
    <div className={styles.gal}>
      <div className={styles.galHead}>
        <button
          type="button"
          className={styles.galBackBtn}
          onClick={showFullGrid ? () => setBrowsingFull(false) : onBack}
        >
          {showFullGrid ? "← Back to suggestions" : "← Back to slides"}
        </button>
        <span className={styles.galCrumb}>
          <span className={styles.galCrumbMeta}>
            SLIDE {position} · {choice.slide.intent_tag}
          </span>
          <span className={styles.galCrumbTitle}>{choice.slide.title}</span>
        </span>
        {onUseAuto && (
          <button type="button" className={styles.galAutoBtn} onClick={onUseAuto}>
            ✦ Use the recommended layout
          </button>
        )}
      </div>

      {showFullGrid ? (
        <>
          <div className={styles.galSearchRow}>
            <input
              className={styles.galSearch}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search layouts"
              aria-label="Search layouts"
            />
            <span className={styles.galCount}>{ordered.length} layouts</span>
          </div>

          {chips.length > 0 && (
            <div className={styles.galChips}>
              <button
                type="button"
                className={`${styles.galChip} ${category === null ? styles.galChipActive : ""}`}
                onClick={() => setCategory(null)}
              >
                <span>All</span>
                <span className={styles.galChipCount}>{templates.length}</span>
              </button>
              {chips.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  className={`${styles.galChip} ${category === chip.id ? styles.galChipActive : ""}`}
                  onClick={() => setCategory(chip.id)}
                >
                  <span>{chip.id}</span>
                  <span className={styles.galChipCount}>{chip.count}</span>
                </button>
              ))}
            </div>
          )}

          <div ref={gridWrapRef} className={styles.galGrid}>
            <Grid
              cellComponent={GalleryCell}
              cellProps={cellProps}
              columnCount={columnCount}
              columnWidth={columnWidth}
              rowCount={rowCount}
              rowHeight={rowHeight}
              // Only matters before the first real measurement (initial paint /
              // a test environment with no layout engine) — style.height above
              // is what actually governs it once mounted in a real browser.
              defaultHeight={400}
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </>
      ) : (
        <>
          {catalogState.status === "error" && (
            <output className={styles.olNotice}>
              <span>
                Couldn&apos;t load the full layout library, so only the AI&apos;s own suggestions are pickable
                below. {catalogState.message}
              </span>
              <button type="button" className={styles.olBtn} onClick={onRetryCatalog}>
                Retry
              </button>
            </output>
          )}
          {/* The endpoint's own suggestions ONLY — see this component's
              header and SuggestedLayoutsGrid. Browsing the rest of the
              (possibly 1000+ entry) library is the button below, never
              automatic. */}
          <SuggestedLayoutsGrid
            suggestions={choice.suggestions}
            templatesById={templatesById}
            activeTemplateId={choice.templateId}
            onPick={onPick}
          />
          <button
            type="button"
            className={styles.galAutoBtn}
            onClick={() => {
              onBrowseLibrary();
              setBrowsingFull(true);
            }}
            disabled={catalogState.status === "loading" || catalogState.status === "error"}
          >
            {browseLibraryLabel(catalogState, templates.length)}
          </button>
        </>
      )}
    </div>
  );
}

/* ── The step ───────────────────────────────────── */

function OutlineErrorView({ message, onRetry }: Readonly<{ message: string; onRetry: () => void }>) {
  return (
    <output className={styles.outlineError}>
      <span>{message}</span>
      <button type="button" className={styles.olBtn} onClick={onRetry}>
        Try again
      </button>
    </output>
  );
}

function OutlineLoadingView() {
  return (
    <output className={styles.olLoading}>
      <span className={styles.olSpinner} aria-hidden="true" />
      <span>Sketching your outline and matching layouts…</span>
    </output>
  );
}

/** The hint line above the row list: the unresolved-slides blocker takes
 *  priority over everything else, since it's what's keeping "Next" disabled.
 *  Swapping is available immediately regardless of whether the full library
 *  has finished loading yet — see ChooseLayoutGallery's quick-pick mode — so
 *  this no longer needs to branch on catalog readiness. */
function OutlineHint({ unresolvedCount }: Readonly<{ unresolvedCount: number }>) {
  if (unresolvedCount > 0) {
    return (
      <p className={styles.olHint}>
        <strong>
          {unresolvedCount} slide{unresolvedCount === 1 ? "" : "s"} still need
          {unresolvedCount === 1 ? "s" : ""} a layout.
        </strong>{" "}
        The ranker had no match — pick one to continue.
      </p>
    );
  }
  return (
    <p className={styles.olHint}>
      AI sketched the outline below. <strong>Click any slide</strong> to swap its layout from the
      library, or accept the auto-pick.
    </p>
  );
}

/** Non-blocking: the outline and its layout picks are all present, and
 *  swapping still works off the AI's own suggestions (ChooseLayoutGallery's
 *  quick-pick mode) — the library only adds real thumbnails and the rest of
 *  the browsable catalog. */
function CatalogNotice({ message, onRetry }: Readonly<{ message: string; onRetry: () => void }>) {
  return (
    <output className={styles.olNotice}>
      <span>
        The full layout library is unavailable, but you can still swap using the AI&apos;s own
        suggestions. {message}
      </span>
      <button type="button" className={styles.olBtn} onClick={onRetry}>
        Retry
      </button>
    </output>
  );
}

function OutlineToolbar({
  onRegenerate,
  onAutoSelectAll,
  onAddSlide,
  canAddSlide,
}: Readonly<{
  onRegenerate: () => void;
  onAutoSelectAll: () => void;
  onAddSlide: () => void;
  canAddSlide: boolean;
}>) {
  return (
    <span className={styles.olToolbar}>
      <button type="button" className={styles.olBtn} onClick={onRegenerate}>
        ↻ Regenerate
      </button>
      <button type="button" className={styles.olBtn} onClick={onAutoSelectAll}>
        ✦ Auto-select all
      </button>
      <button type="button" className={styles.olBtn} onClick={onAddSlide} disabled={!canAddSlide}>
        + Add slide
      </button>
    </span>
  );
}

interface StepPickLayoutsProps {
  readonly picker: TemplatePicker;
}

export function StepPickLayouts({ picker }: Readonly<StepPickLayoutsProps>) {
  const { outlineState, catalogState, choices } = picker;
  /** slide_idx of the row whose layout is being swapped, or null for the list. */
  const [swapping, setSwapping] = useState<number | null>(null);

  // ONLY the outline gates this step. The library is enrichment — see
  // hooks/useTemplatePicker.ts's header — so its failure must not hide an
  // outline that arrived.
  if (outlineState.status === "error") {
    return <OutlineErrorView message={outlineState.message} onRetry={picker.regenerate} />;
  }

  if (outlineState.status !== "ready") {
    return <OutlineLoadingView />;
  }

  // Swapping no longer waits on the full catalog — see ChooseLayoutGallery's
  // quick-pick mode, which works off the outline's own ranked suggestions.
  const swapChoice = choices.find((choice) => choice.slide.slide_idx === swapping);
  if (swapping !== null && swapChoice) {
    const position = choices.indexOf(swapChoice) + 1;
    const autoSuggestion = swapChoice.suggestions[0];
    return (
      <ChooseLayoutGallery
        choice={swapChoice}
        position={position}
        catalogState={catalogState}
        templatesById={picker.templatesById}
        onPick={(templateId) => {
          picker.choose(swapChoice.slide.slide_idx, templateId);
          setSwapping(null);
        }}
        onBack={() => setSwapping(null)}
        onUseAuto={
          autoSuggestion && autoSuggestion.id !== swapChoice.templateId
            ? () => {
                picker.choose(swapChoice.slide.slide_idx, autoSuggestion.id);
                setSwapping(null);
              }
            : null
        }
        onRetryCatalog={picker.retryCatalog}
        onBrowseLibrary={picker.requestLibrary}
      />
    );
  }

  return (
    <>
      <div className={styles.olHead}>
        <h3 className={styles.stepTitle}>Pick a layout for each slide</h3>
        <OutlineToolbar
          onRegenerate={picker.regenerate}
          onAutoSelectAll={picker.autoSelectAll}
          onAddSlide={() => picker.addSlide(null)}
          canAddSlide={picker.canAddSlide}
        />
      </div>

      <OutlineHint unresolvedCount={picker.unresolvedCount} />

      {catalogState.status === "error" && (
        <CatalogNotice message={catalogState.message} onRetry={picker.retryCatalog} />
      )}

      <div className={styles.olList}>
        {choices.map((choice, index) => (
          <SlideRow
            key={choice.slide.slide_idx}
            choice={choice}
            position={index + 1}
            isFirst={index === 0}
            isLast={index === choices.length - 1}
            canRemove={choices.length > 1}
            // Opens the outline's own suggestions only — no library load.
            onChoose={() => setSwapping(choice.slide.slide_idx)}
            onMoveUp={() => picker.moveSlide(choice.slide.slide_idx, "up")}
            onMoveDown={() => picker.moveSlide(choice.slide.slide_idx, "down")}
            onRemove={() => picker.removeSlide(choice.slide.slide_idx)}
          />
        ))}
      </div>
    </>
  );
}
